import type { PermissionAction } from '@rochadenis/axiomgo-contracts'
import { capturePermissionSubject, usePermissionsStore } from '@/stores/permissionsStore'

const READ_KINDS = new Set([
  'view_history',
  'open_panel',
  'search_manufacturer',
  'list_more',
  'page',
  'sort',
  'filter',
  'row_detail',
  'export',
])
const READ_IDS = new Set([
  'card.equipment.search_manufacturer',
  'card.equipment_registration.view',
  'equipment_search_result.list_more',
  'card.equipment.list_more',
  'notification_list.list_more',
  'card.notification.list_more',
])
const WRITE_CHANNELS = [
  'equipmentRegistrationAction',
  'preventivePlanAction',
  'correctiveAction',
  'manualPlanAction',
  'notificationAction',
  'importFlowAction',
  'onboardingAction',
  'planComparisonAction',
  'procedureAuthoringAction',
  'photoPolicyAction',
  'confirmationToken',
  'closeNote',
  'closingNoteWandAction',
  'command',
]
const AGENT_READ_KINDS = new Set(['open_panel', 'open_policy', 'explain', 'audit'])
/** Kairos: responder a pergunta e pedir explicação não gravam nada; o resto grava ou move. */
const KAIROS_READ_ACTIONS = new Set(['answer', 'explain'])
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/** Visual defense only. The engine must authorize every mutation independently. */
export function canChatWrite(
  resource = 'work_order',
  action: PermissionAction = 'update',
): boolean {
  return usePermissionsStore.getState().can(resource, action)
}

/** Explicit read contracts survive; an untyped legacy chip is not proof of read authority. */
export function isReadChatAction(value: unknown): boolean {
  const action = record(value)
  if (WRITE_CHANNELS.some((key) => action[key] !== undefined)) return false
  if (action.agentAction) return action.orderExecutionAction === undefined && AGENT_READ_KINDS.has(String(record(action.agentAction).kind))
  if (action.kairosAction) return KAIROS_READ_ACTIONS.has(String(record(action.kairosAction).action))
  const payload = action.structuredAction ?? action.carga
  if (payload) {
    const structured = record(payload)
    return isReadChatAction(structured.orderExecutionAction ?? structured)
  }
  if (typeof action.kind === 'string' && READ_KINDS.has(action.kind)) return true
  const id = action.intentId ?? action.actionId ?? action.id
  return (
    typeof id === 'string' &&
    (READ_IDS.has(id) || id.startsWith('insights.') || id.startsWith('auditor.'))
  )
}

export function canChatAction(action: unknown): boolean {
  return !usePermissionsStore.getState().isReadOnly() || isReadChatAction(action)
}

/** Re-check live role, rather than the role captured when an old card rendered. */
export function canSendChatAction(options: unknown): boolean {
  if (!usePermissionsStore.getState().isReadOnly()) return true
  const wire = record(options)
  if (WRITE_CHANNELS.some((key) => wire[key] !== undefined)) return false
  if (wire.agentAction) return wire.orderExecutionAction === undefined && AGENT_READ_KINDS.has(String(record(wire.agentAction).kind))
  if (wire.kairosAction) return KAIROS_READ_ACTIONS.has(String(record(wire.kairosAction).action))
  return wire.orderExecutionAction === undefined || isReadChatAction(wire.orderExecutionAction)
}

export function cloneChatAction<T>(action: T): T {
  return action === undefined ? action : structuredClone(action)
}

/** Actor binding applies to every turn; canonical write authority only to write channels. */
export function captureChatSendAuthorization(options: unknown, tenantId: string, userId: string) {
  const ownsSubject = capturePermissionSubject(tenantId, userId)
  const action = cloneChatAction(options)
  return (): 'allowed' | 'denied' | 'stale' =>
    !ownsSubject() ? 'stale' : canSendChatAction(action) ? 'allowed' : 'denied'
}
